import { useLocation, useNavigate } from "react-router";
import styled, { keyframes } from "styled-components";
import { Pathname } from "../../types/pathname";
import { toast } from "react-toastify";

interface Props {
  menuMap: { [key: string]: Pathname };
  label: string;
  icon?: React.ReactNode;
}

export const HeaderMenu: React.FC<Props> = ({ label, menuMap, icon }) => {
  const history = useNavigate();
  const location = useLocation();

  const move = (event: React.MouseEvent<HTMLButtonElement>) => {
    const targetPath = event.currentTarget.name;
    if (location.pathname === targetPath) {
      toast.error("동일한 페이지를 보고 있어요");
    } else {
      history(targetPath);
    }
  };

  return (
    <MenuContainer>
      <TabButton>
        <TabButtonContent>
          {icon}
          {label}
          <ExpandIcon>▾</ExpandIcon>
        </TabButtonContent>
      </TabButton>
      <MegaMenuContent className="menu-content">
        <MenuInner>
          <MenuHeader>
            <MenuTitle>{label}</MenuTitle>
            <MenuDivider />
          </MenuHeader>
          <MenuGrid>
            {Object.keys(menuMap).map((key) => (
              <MenuButton key={key} name={menuMap[key]} onClick={move}>
                <MenuItemContent>
                  <MenuItemTitle>{key}</MenuItemTitle>
                  <MenuItemIcon>→</MenuItemIcon>
                </MenuItemContent>
              </MenuButton>
            ))}
          </MenuGrid>
        </MenuInner>
      </MegaMenuContent>
    </MenuContainer>
  );
};

const slideInAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const MenuContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  
  &:hover .menu-content {
    display: block;
  }
`;

const TabButton = styled.button`
  font-weight: 600;
  min-width: 64px;
  height: 56px;
  background-color: transparent;
  font-size: 0.875rem;
  line-height: 1.75;
  letter-spacing: 0.02857em;
  border: none;
  color: #ffffff;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  padding: 0 12px;
  
  @media (max-width: 768px) {
    width: 100%;
    text-align: left;
    padding: 12px 24px;
    height: auto;
  }
  
  &:hover {
    color: #fcca00;
  }
`;

const TabButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
  }
  
  svg {
    font-size: 16px;
  }
`;

const ExpandIcon = styled.span`
  font-size: 14px;
  line-height: 1;
  margin-top: 1px;
  transition: transform 0.2s ease;
  
  ${MenuContainer}:hover & {
    transform: rotate(180deg);
  }
`;

const MegaMenuContent = styled.div`
  position: absolute;
  top: 56px;
  left: 0;
  width: 140px;
  background: rgba(28, 32, 38, 0.98);
  backdrop-filter: blur(8px);
  border-radius: 8px;
  z-index: 1000000;
  display: none;
  animation: ${slideInAnimation} 0.2s ease-out;
  box-shadow: 
    0 4px 12px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  overflow: hidden;

  @media (max-width: 768px) {
    position: static;
    width: 100%;
    box-shadow: none;
    border-radius: 0;
    background: transparent;
  }
`;

const MenuInner = styled.div`
  padding: 8px;
`;

const MenuHeader = styled.div`
  text-align: center;
`;

const MenuTitle = styled.h3`
  color: #fcca00;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  display: inline-block;
`;

const MenuDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    to right,
    rgba(252, 202, 0, 0.3),
    rgba(252, 202, 0, 0.1)
  );
  margin-top: 8px;
  margin-bottom: 8px;
`;

const MenuGrid = styled.div`
  display: grid;
  gap: 4px;
`;

const MenuButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background-color: transparent;
  font-size: 0.875rem;
  font-weight: 500;
  color: #ffffff;
  border: none;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s ease;
  text-align: left;

  @media (max-width: 768px) {
    padding: 12px 24px;
  }

  &:hover {
    background: rgba(252, 202, 0, 0.1);
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

const MenuItemTitle = styled.span`
  color: #ffffff;
  font-weight: 500;
`;

const MenuItemIcon = styled.span`
  color: #fcca00;
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.2s ease;

  ${MenuButton}:hover & {
    opacity: 1;
    transform: translateX(0);
  }
`;
